"use client";

import React, { useEffect, useState, useMemo } from "react";
import {
  Search,
  User,
  Wifi,
  Calendar,
  CheckCircle2,
  XCircle,
  Copy,
  Trash2,
  MoreVertical,
  RefreshCw,
  Router as RouterIcon,
  Ticket,
} from "lucide-react";
import { fetchData } from "../../../services/apiService";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";

// --- Types ---
interface Plan {
  id: number;
  name: string;
  price: number;
}

interface RouterRef {
  id: number;
  name: string;
}

interface HotspotUser {
  id: number;
  username: string;
  password: string;
  status: boolean;
  created_at: string;
  activated_at?: string | null;
  hotspot_plan?: Plan | null;
  router?: RouterRef | null;
}

// --- Helpers ---
const formatDate = (dateStr: string) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-KE", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const HotspotUsersPage: React.FC = () => {
  const [users, setUsers] = useState<HotspotUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    user: HotspotUser | null;
  }>({
    open: false,
    user: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  // --- Actions ---
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await fetchData<HotspotUser[]>("/hotspot-users");
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch users", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    // Optional: Add a small toast notification here
  };

  const handleDelete = async () => {
    if (!deleteDialog.user) return;
    setIsDeleting(true);
    try {
      await fetchData(`/hotspot-users/${deleteDialog.user.id}`, {
        method: "DELETE",
      });
      setUsers(users.filter((u) => u.id !== deleteDialog.user?.id));
      setDeleteDialog({ open: false, user: null });
    } catch (err) {
      alert("Failed to delete user");
    } finally {
      setIsDeleting(false);
    }
  };

  // --- Filtering ---
  const filteredUsers = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return users.filter(
      (u) =>
        u.username.toLowerCase().includes(q) ||
        (u.hotspot_plan?.name || "").toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 md:p-10 font-sans">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            Hotspot Users
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Active vouchers and generated user credentials.
          </p>
        </div>

        <div className="flex gap-3">
          <div className="relative group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              placeholder="Search username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-10 pl-10 pr-4 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 w-64 shadow-sm"
            />
          </div>
          <button
            onClick={fetchUsers}
            className="h-10 w-10 flex items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-300 transition shadow-sm"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-6 py-4 font-semibold text-slate-500">
                  User / Creds
                </th>
                <th className="px-6 py-4 font-semibold text-slate-500">
                  Plan Details
                </th>
                <th className="px-6 py-4 font-semibold text-slate-500">
                  Status
                </th>
                <th className="px-6 py-4 font-semibold text-slate-500">
                  Created At
                </th>
                <th className="px-6 py-4 font-semibold text-slate-500 text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                // Skeleton Loader
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4">
                      <div className="h-4 bg-slate-100 rounded w-32"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-slate-100 rounded w-24"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-slate-100 rounded w-16"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-slate-100 rounded w-24"></div>
                    </td>
                    <td className="px-6 py-4"></td>
                  </tr>
                ))
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-12 text-center text-slate-500"
                  >
                    No users found matching your search.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="group hover:bg-slate-50/50 transition-colors"
                  >
                    {/* User Credentials */}
                    <td className="px-6 py-4 align-top">
                      <div className="flex items-start gap-3">
                        <div className="h-8 w-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 mt-0.5">
                          <User className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900">
                              {user.username}
                            </span>
                            <button
                              onClick={() => handleCopy(user.username)}
                              className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-500 transition-opacity"
                              title="Copy Username"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                          </div>
                          <div className="flex items-center gap-1 mt-1 text-slate-500 text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded w-fit">
                            <span>pw: {user.password}</span>
                            <button
                              onClick={() => handleCopy(user.password)}
                              className="text-slate-400 hover:text-blue-500 ml-1"
                              title="Copy Password"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Plan Details */}
                    <td className="px-6 py-4 align-top">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-slate-700 font-medium">
                          <Ticket className="h-3.5 w-3.5 text-slate-400" />
                          {user.hotspot_plan
                            ? user.hotspot_plan.name
                            : "No Plan"}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <RouterIcon className="h-3.5 w-3.5 text-slate-400" />
                          {user.router ? user.router.name : "Unknown Router"}
                        </div>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4 align-top">
                      <div
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                          user.status
                            ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                            : "bg-slate-50 text-slate-600 border-slate-200"
                        }`}
                      >
                        {user.status ? (
                          <>
                            <CheckCircle2 className="h-3.5 w-3.5" /> Active
                          </>
                        ) : (
                          <>
                            <XCircle className="h-3.5 w-3.5" /> Inactive
                          </>
                        )}
                      </div>
                      {user.activated_at && (
                        <div className="mt-1 text-[10px] text-slate-400 pl-1">
                          Active since:{" "}
                          {formatDate(user.activated_at).split(",")[0]}
                        </div>
                      )}
                    </td>

                    {/* Created Date */}
                    <td className="px-6 py-4 align-top text-slate-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5 text-slate-400" />
                        {formatDate(user.created_at)}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 align-top text-right">
                      <button
                        onClick={() => setDeleteDialog({ open: true, user })}
                        className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete User"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer (Static for now) */}
        {!loading && filteredUsers.length > 0 && (
          <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex justify-between items-center text-xs text-slate-500">
            <span>Showing {filteredUsers.length} results</span>
            {/* Add pagination buttons here if backend supports skipping */}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteDialog.open}
        loading={isDeleting}
        title="Delete User"
        description={`Are you sure you want to delete user "${deleteDialog.user?.username}"? This will disconnect them if they are currently active.`}
        variant="danger"
        confirmLabel="Delete User"
        onCancel={() => setDeleteDialog({ open: false, user: null })}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default HotspotUsersPage;
